import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { getIntl } from '../../../../../utils/intl';
import { useCollaborators } from '../../../../../hooks/api/useCollaborators';
import getName from '../../../../../utils/getName';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import { useLoadedUser } from '../../../../../hooks/api/useUser';
import useProfileTransferRequest from '../../../../../hooks/api/useProfileTransferRequest';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import ProfileOwnerAvatar from '../../../../v2BaseComponents/ProfileOwnerAvatar';
import { composeValidators, consentCheck, email, required, valuesMustMatch } from '../../../../../utils/validators';
import { Field } from 'formik';
import { handleFormikSubmit } from '../../../../../utils/handleFormikSubmit';
import { CenteredFormRow, Form, SplitFormRow } from '../../../../v2BaseComponents/Form';
import Text from '../../../../v2BaseComponents/Text';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import Stack, { FormStack } from '../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { SelectOption } from '../../../../v2BaseComponents/Inputs/Select/types';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';

const intl = getIntl('createTransferRequest', {
    detailsText:
        'Transfer ownership of this profile to another collaborator or user email by creating a transfer request.  Upon receiving the request, the recipient will have the option to accept or deny the request.  If denied, you will maintain ownership of the profile.  If accepted, the recipient will then own the profile.  You can request to maintain access as a collaborator, but the new owner makes the final decision for you to maintain access as a collaborator.',
    selectTitle: 'New Organizer',
    email: 'New Organizer Email',
    verifyEmail: 'Verify New Organizer Email',
    maintainAccessTitle: "I'd like to maintain access",
    maintainAccessHelperText:
        "Let the new organizer know you'd like to maintain access as a collaborator.  They will decide if you can maintain access.",
    consent:
        'As {profileFirstName}’s current Administrator, I, {userFullName} confirm I have the authority and permission to transfer {profileFullName} to the authority of {toUser}. I understand this transfer will be logged in the history of {profileFullName}’s Primary Record and I may lose all access to {profileFirstName}’s Primary Record Profile. I may be invited in to collaborate and support {toUser}, but only if they grant me permission.',
    sendTransferRequest: 'Send Transfer Request',
    noCollaboratorsError: 'Add a collaborator to send a transfer request',
    transferToCollaborator: 'Transfer ownership to collaborator?',
    emailsMustMatch: 'Emails must match',
    message: 'Include a message to the new organizer',
    title: 'Create Transfer Request',
});

export type AddTransferRequestScreenOwnProps = {};

export type AddTransferRequestScreenProps = AddTransferRequestScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'add-transfer-request'>;
};

type FormValues = {
    transferToCollaborator: boolean;
    selectedCollaboratorId?: string;
    nonCollaboratorEmail?: string;
    nonCollaboratorVerifiedEmail?: string;
    consentAccepted: boolean;
    maintainAccess: boolean;
    message: string;
};

const AddTransferRequestScreen: React.FC<AddTransferRequestScreenProps> = (props) => {
    const { user } = useLoadedUser({});
    const goBack = useGoBack();
    const { profile } = useMedicalProfile({
        medicalProfileId: props.route.params.profileId,
    });
    const { isLoadingCollaborators, userCollaborators } = useCollaborators({
        medicalProfileId: props.route.params.profileId,
    });
    const { sendTransferRequest, isSendingTransferRequest } = useProfileTransferRequest({
        profileId: props.route.params.profileId,
        onSendTransferRequest: () => {
            goBack();
        },
    });

    const initialValues = useMemo<FormValues | null>(() => {
        if (isLoadingCollaborators) return null;

        if (
            !props.route.params.collaboratorId ||
            !userCollaborators.find((_) => _.medicalProfileCollaboratorId === props.route.params.collaboratorId)
        ) {
            return {
                transferToCollaborator: false,
                nonCollaboratorEmail: '',
                nonCollaboratorVerifiedEmail: '',
                consentAccepted: false,
                maintainAccess: false,
                message: '',
            };
        } else {
            return {
                transferToCollaborator: true,
                nonCollaboratorEmail: '',
                nonCollaboratorVerifiedEmail: '',
                consentAccepted: false,
                maintainAccess: false,
                message: '',
                selectedCollaboratorId: props.route.params.collaboratorId,
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingCollaborators]);

    const getConsent = (formValues: FormValues) => {
        if (!profile) return null;

        if (formValues.transferToCollaborator && !formValues.selectedCollaboratorId) return null;

        if (
            !formValues.transferToCollaborator &&
            (!formValues.nonCollaboratorEmail || !!email(formValues.nonCollaboratorEmail))
        )
            return null;

        return intl('consent', {
            profileFirstName: profile.contactDetails.firstName || '',
            userFullName: getName(user),
            profileFullName: getName(profile.contactDetails),
            toUser: formValues.transferToCollaborator
                ? getName(
                      userCollaborators.find(
                          (_) => _.medicalProfileCollaboratorId === formValues.selectedCollaboratorId,
                      )!.user,
                  )
                : formValues.nonCollaboratorEmail!,
        });
    };

    const options: SelectOption[] = userCollaborators
        .map((_) => ({
            label: getName(_.user),
            value: _.medicalProfileCollaboratorId,
            testID: getName(_.user),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <AnimateLoadedContent isLoading={isLoadingCollaborators || !initialValues || isSendingTransferRequest}>
            {() => (
                <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder omitChatButton>
                    <Form
                        actionContainerSubmit
                        initialValues={initialValues!}
                        title={intl('title')}
                        onCancel={goBack}
                        cancelButtonVariant="secondary"
                        onSubmit={(values, helpers) => {
                            if (values.transferToCollaborator) {
                                sendTransferRequest({
                                    maintainAccess: values.maintainAccess,
                                    collaboratorId: values.selectedCollaboratorId!,
                                    profileId: props.route.params.profileId,
                                    message: values.message,
                                });
                            } else {
                                sendTransferRequest({
                                    maintainAccess: values.maintainAccess,
                                    email: values.nonCollaboratorEmail!,
                                    profileId: props.route.params.profileId,
                                    message: values.message,
                                });
                            }

                            handleFormikSubmit(values, helpers);
                        }}
                    >
                        {({ values }) => {
                            return (
                                <FormStack>
                                    <CenteredFormRow>
                                        <Text variant="body-2">{intl('detailsText')}</Text>
                                    </CenteredFormRow>
                                    <CenteredFormRow>
                                        <ProfileOwnerAvatar medicalProfileId={props.route.params.profileId} />
                                    </CenteredFormRow>
                                    {options.length ? (
                                        <CenteredFormRow>
                                            <Field
                                                component={FormikInputs.Switch}
                                                name="transferToCollaborator"
                                                title={intl('transferToCollaborator')}
                                            />
                                        </CenteredFormRow>
                                    ) : null}
                                    <SplitFormRow>
                                        {values.transferToCollaborator ? (
                                            <Field
                                                name="selectedCollaboratorId"
                                                component={FormikInputs.Select}
                                                selectOptions={options}
                                                validate={required}
                                                title={intl('selectTitle')}
                                                isRequired
                                            />
                                        ) : null}
                                    </SplitFormRow>
                                    {!values.transferToCollaborator ? (
                                        <SplitFormRow>
                                            <Field
                                                name="nonCollaboratorEmail"
                                                title={intl('email')}
                                                component={FormikInputs.Input}
                                                validate={composeValidators(
                                                    required,
                                                    email,
                                                    values.nonCollaboratorVerifiedEmail
                                                        ? valuesMustMatch(
                                                              values.nonCollaboratorVerifiedEmail,
                                                              intl('emailsMustMatch'),
                                                          )
                                                        : () => {
                                                              return undefined;
                                                          },
                                                )}
                                            />
                                            {values.nonCollaboratorEmail && !email(values.nonCollaboratorEmail) ? (
                                                <Field
                                                    name="nonCollaboratorVerifiedEmail"
                                                    title={intl('verifyEmail')}
                                                    component={FormikInputs.Input}
                                                    validate={composeValidators(
                                                        required,
                                                        email,
                                                        valuesMustMatch(
                                                            values.nonCollaboratorEmail,
                                                            intl('emailsMustMatch'),
                                                        ),
                                                    )}
                                                />
                                            ) : null}
                                        </SplitFormRow>
                                    ) : null}
                                    <SplitFormRow>
                                        <Field
                                            component={FormikInputs.TextArea}
                                            title={intl('message')}
                                            name="message"
                                        />
                                    </SplitFormRow>
                                    <CenteredFormRow>
                                        <Stack vertical fullWidth>
                                            <Field
                                                component={FormikInputs.Checkbox}
                                                name="maintainAccess"
                                                title={intl('maintainAccessTitle')}
                                            />
                                            <Text variant="caption-1">{intl('maintainAccessHelperText')}</Text>
                                        </Stack>
                                    </CenteredFormRow>
                                    <CenteredFormRow>
                                        {getConsent(values) ? (
                                            <Field
                                                name="consentAccepted"
                                                component={FormikInputs.Checkbox}
                                                title={getConsent(values)}
                                                topAlignCheckbox
                                                validate={consentCheck}
                                            />
                                        ) : null}
                                    </CenteredFormRow>
                                </FormStack>
                            );
                        }}
                    </Form>
                </ProfileHeaderContainer>
            )}
        </AnimateLoadedContent>
    );
};

const RemountingForm: React.FC<AddTransferRequestScreenProps> = (props) => {
    return <AddTransferRequestScreen {...props} key={JSON.stringify(props.route.params)} />;
};

export default RemountingForm;
